import { computed } from 'vue'
import type { ComputedRef } from 'vue'
import { useSessionStore } from '@/apps/session/stores/session'
import type { OrgPermission } from '@/types/permissions'

export interface UseCanReturn {
  /** Whether the caller holds `permission` in the organization the URL names. */
  can: (permission: OrgPermission) => boolean
  /** Whether they hold at least one of `permissions`. */
  hasAny: (...permissions: OrgPermission[]) => boolean
  /** Whether they hold every one of `permissions`. */
  hasAll: (...permissions: OrgPermission[]) => boolean
  /** The effective keys, sorted — the set the server checks each request against. */
  permissions: ComputedRef<readonly string[]>
  /** Whether `GET /api/app/access` has not answered yet. */
  isLoading: ComputedRef<boolean>
}

/**
 * Permission gating inside the organization the URL names: a reading of the session, which the
 * router filled with that organization's effective set before any view of it mounted.
 *
 * **Keyed by permission, never by role.** An organization's own roles have no name a client may
 * branch on, and an owner holds what it holds through the same set — there is no short-circuit
 * to keep in step with the server's.
 *
 * Organization keys only. A workspace key is held one scope down and is never in this set, so a
 * gate on one reads the role the caller holds in that workspace instead.
 */
export function useCan(): UseCanReturn {
  const session = useSessionStore()
  const can = (permission: OrgPermission): boolean => session.can(permission)

  return {
    can,
    hasAny: (...permissions) => permissions.some((permission) => can(permission)),
    hasAll: (...permissions) => permissions.every((permission) => can(permission)),
    permissions: computed(() => session.access?.org?.permissions ?? []),
    isLoading: computed(() => session.access === null),
  }
}
